import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, Tag, ChevronRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function BlogList() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 12;

  useEffect(() => {
    fetchPosts();
  }, [page]);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/api/blog?skip=${page * limit}&limit=${limit}`);
      setPosts(response.data.posts);
      setTotal(response.data.total);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching blog posts:", error);
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading blog posts...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white">
      <Helmet>
        <title>DFW Real Estate Blog | FSBO Tips & Home Selling Guides | Its-Sold.com</title>
        <meta name="description" content="Expert FSBO tips, DFW real estate insights, and money-saving strategies for Texas homeowners. Learn how to sell your home without an agent." />
        <link rel="canonical" href="https://its-sold.com/blog" />
      </Helmet>
      
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>
            Its-Sold.com Blog
          </h1>
          <p className="text-xl text-orange-100 max-w-3xl mx-auto">
            Expert FSBO tips, DFW real estate insights, and money-saving strategies for Texas homeowners
          </p>
          <div className="mt-6 flex items-center justify-center space-x-2 text-orange-100">
            <Tag className="w-5 h-5" />
            <span className="text-sm">FSBO Tips • Real Estate • DFW Market • Home Selling</span>
          </div>
        </div>
      </div>

      {/* Blog Posts Grid */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        {posts.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-xl text-gray-600">No blog posts yet. Check back soon!</p>
          </div>
        ) : (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post) => (
                <Card 
                  key={post.slug} 
                  className="overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:-translate-y-2"
                  onClick={() => navigate(`/blog/${post.slug}`)}
                >
                  {/* Featured Image */}
                  <div className="h-48 bg-gradient-to-br from-orange-400 to-red-500 relative overflow-hidden">
                    <img 
                      src={post.image_url} 
                      alt={post.title}
                      className="w-full h-full object-cover opacity-90"
                      onError={(e) => {
                        e.target.src = 'https://placehold.co/600x400/FF6B35/FFFFFF/png?text=Its-Sold.com+Blog';
                      }}
                    />
                    <div className="absolute top-4 right-4 bg-white px-3 py-1 rounded-full text-xs font-semibold text-orange-600">
                      {post.category}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 hover:text-orange-600 transition">
                      {post.title}
                    </h2>
                    
                    <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>

                    {/* Meta */}
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-1">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(post.publish_date)}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Clock className="w-4 h-4" />
                          <span>{post.read_time} min read</span>
                        </div>
                      </div>
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {post.tags.slice(0, 3).map((tag, idx) => (
                        <span 
                          key={idx}
                          className="bg-orange-100 text-orange-700 px-2 py-1 rounded text-xs"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    {/* Read More */}
                    <Button 
                      variant="ghost" 
                      className="w-full text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                    >
                      Read Full Article
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>

            {/* Pagination */}
            {total > limit && (
              <div className="mt-12 flex justify-center items-center space-x-4">
                <Button
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                  variant="outline"
                >
                  Previous
                </Button>
                <span className="text-gray-600">
                  Page {page + 1} of {Math.ceil(total / limit)}
                </span>
                <Button
                  onClick={() => setPage(page + 1)}
                  disabled={(page + 1) * limit >= total}
                  variant="outline"
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-orange-600 to-red-600 py-16 mt-20">
        <div className="max-w-4xl mx-auto px-4 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Ready to Sell Your Home Without a Realtor?</h2>
          <p className="text-xl text-orange-100 mb-8">
            Join thousands of DFW homeowners who saved thousands on commission
          </p>
          <Button 
            size="lg"
            onClick={() => navigate('/register')}
            className="bg-white text-orange-600 hover:bg-gray-100 text-lg px-8 py-6"
          >
            List Your Home FREE
          </Button>
        </div>
      </div>
    </div>
  );
}
